import React, { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { ptBR } from "date-fns/locale";
import { cn } from "../../lib/utils";

/**
 * Calendário no modelo do shadcn/ui: o mesmo react-day-picker v9, mas estilizado só com classes e
 * tokens do tema. NÃO importe "react-day-picker/style.css" — aquele CSS tem cores próprias e
 * ignora o tema claro/escuro, que era exatamente o defeito do calendário anterior.
 *
 * Seleção de período: as pontas (início e fim) ficam sólidas em foreground; os dias do meio, numa
 * faixa neutra (accent). Sem vermelho da marca: ele é só de botão (decisão do dono em 2026-09-15).
 */
const NAV_BUTTON =
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors " +
  "hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring " +
  "disabled:pointer-events-none disabled:opacity-40";

function CalendarChevron({ orientation, className, ...props }) {
  const Icon = orientation === "left" ? ChevronLeft : ChevronRight;
  return <Icon className={cn("h-4 w-4", className)} aria-hidden="true" {...props} />;
}

function CalendarDayButton({ className, day, modifiers, ...props }) {
  const ref = useRef(null);

  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const inRange = modifiers.range_start || modifiers.range_end || modifiers.range_middle;
  const single = modifiers.selected && !inRange;
  const edge = modifiers.range_start || modifiers.range_end || single;

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString("pt-BR")}
      className={cn(
        "flex h-9 w-full items-center justify-center rounded-md text-sm tabular-nums transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none",
        edge
          ? "bg-foreground font-semibold text-background hover:bg-foreground/90"
          : modifiers.range_middle
            ? "rounded-none text-foreground hover:bg-foreground/10"
            : "text-foreground hover:bg-accent hover:text-accent-foreground",
        modifiers.today && !edge && "font-bold text-foreground underline underline-offset-4",
        modifiers.outside && !edge && !modifiers.range_middle && "text-muted-foreground/60",
        className
      )}
      {...props}
    />
  );
}

export default function Calendar({ className, classNames, showOutsideDays = true, locale = ptBR, ...props }) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      className={cn("w-fit p-1", className)}
      classNames={{
        months: "relative flex flex-col gap-4 sm:flex-row",
        month: "flex w-full flex-col gap-3",
        nav: "absolute inset-x-0 top-0 z-10 flex w-full items-center justify-between",
        button_previous: NAV_BUTTON,
        button_next: NAV_BUTTON,
        month_caption: "flex h-8 w-full items-center justify-center px-9",
        caption_label: "select-none text-sm font-semibold capitalize text-foreground",
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "w-9 flex-1 select-none text-center text-[0.7rem] font-medium uppercase text-muted-foreground",
        weeks: "",
        week: "mt-1 flex w-full",
        day: "relative w-9 flex-1 select-none p-0 text-center",
        // Faixa do período na célula; o botão da ponta fica por cima, sólido.
        range_start: "rounded-l-md bg-accent",
        range_middle: "bg-accent",
        range_end: "rounded-r-md bg-accent",
        selected: "",
        today: "",
        outside: "",
        disabled: "opacity-40",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
      }}
      {...props}
    />
  );
}
